/**
 * Le registre d'identité du module Partage — contrat partagé (spec D22).
 *
 * L'IDENTIFIANT VEVIDENTAL VOYAGE EN CHAÎNE, JAMAIS EN `number`.
 *
 * `vevidental_entite.id_source` est un BIGINT, et le pilote Postgres de
 * production rend un `int8` en chaîne là où PGlite — le moteur des tests —
 * rend un nombre. Le dépôt avait déjà tranché sans le dire ici :
 * `id_source::text` partout où il sort (`miroir/ecart.ts` l. 108 et 142,
 * `routes/vevidental-lecture.ts` l. 101).
 *
 * Le typer `number` serait la TROISIÈME occurrence de la classe de défaut qui
 * a rendu l'application inutilisable trois heures le 11/08, et la deuxième
 * après `FinanceSurveille.factureId` le 12/08 — verte sous PGlite, fausse en
 * production, invisible parce qu'AUCUN test de ce dépôt ne tourne sur le
 * pilote de production.
 */
import { z } from "zod";
import type { IdPraticien } from "./partage.js";

/** Identifiant Vevidental : une CHAÎNE de chiffres. Voir l'en-tête. */
export type IdVevidental = string;

/**
 * Un BIGINT et rien d'autre.
 *
 * La borne de longueur n'est pas décorative : sans elle, un `9` répété cent
 * fois passerait la validation puis déborderait le `bigint` côté base — donc
 * une erreur Postgres, donc un 500, pour une saisie de travers.
 *
 * UNE définition, deux usages : ce schéma valide l'entrée réseau, et le
 * magasin du BFF réutilise CETTE regex pour sa propre garde. §17.5 : jamais
 * deux implémentations jumelles.
 */
export const EST_ID_VEVIDENTAL = /^[0-9]{1,18}$/;

export const IdVevidentalSchema = z
  .string()
  .regex(EST_ID_VEVIDENTAL, "identifiant Vevidental invalide : un nombre est attendu");

/** Un dentiste tel qu'on le propose au choix — le geste « je sélectionne ». */
export interface OptionPraticien {
  idVevidental: IdVevidental;
  nom: string;
  /** L'identité de registre, `null` tant que ce dentiste n'en a jamais eu. */
  id: IdPraticien | null;
}

/**
 * Une clinique telle qu'on la propose au choix — le geste « je rattache ce
 * lieu à un cabinet » (etape E6).
 *
 * Pas d'`id` de registre ici, contrairement a `OptionPraticien` : personne
 * n'en a besoin cote ecran, et le serveur cree l'identite au passage.
 */
export interface OptionClinique {
  idVevidental: IdVevidental;
  nom: string;
  /**
   * ⚠ PAS DECORATIF. 1 126 cliniques sur 1 253 n'ont pas de fiche dans le CRM.
   * En choisir une produit un rattachement parfaitement valide dont AUCUNE
   * fiche ne saura jamais rien faire. Le choix reste libre — il ne doit
   * simplement pas etre aveugle.
   */
  aUneFicheCrm: boolean;
}

/**
 * Une ligne de la recherche unifiée — TOUJOURS un cabinet.
 *
 * ⚠ UN SEUL TYPE, ET C'EST LE POINT. Deux types à l'écran, c'est un choix de
 * nature à faire, et 452 noms sur 559 existent des deux côtés : ce choix est
 * une devinette. Un dentiste cherché rend le cabinet auquel il mène.
 */
export interface CabinetTrouve {
  idVevidental: IdVevidental;
  nom: string;
  aUneFicheCrm: boolean;
  /**
   * Faux = la ligne s'affiche, grisée, et ne se clique pas. On ne fait JAMAIS
   * disparaître en silence un nom que l'utilisateur vient de taper : il
   * conclurait que le nom n'existe pas, ce qui est faux.
   */
  actionnable: boolean;
  /** Renseignée seulement quand `actionnable` est faux. */
  raison?: string;
}

/**
 * Pourquoi ce dentiste est propose pour ce cabinet — etape E6 bis.
 *
 * ⚠ CE CHAMP EXISTE PARCE QUE DEUX DES TROIS SOURCES SONT DES INDICES, PAS DES
 * FAITS. Vevidental n'a AUCUN lien fiable cabinet -> dentiste : sur 1 118
 * docteurs de la copie, 153 seulement (13,7 %) portent un cabinet dans leur
 * fiche. Il faut donc croiser trois sources, et elles ne se valent pas :
 *
 *   `travaux`   — un travail reel relie ce cabinet a ce dentiste. C'est un
 *                 FAIT : le laboratoire a deja travaille pour ce couple.
 *   `fiche`     — la fiche Vevidental du docteur cite ce cabinet. Un fait
 *                 declare, non atteste par un travail.
 *   `homonyme`  — le docteur porte EXACTEMENT le nom du cabinet. C'est le cas
 *                 de 959 docteurs sur 1 118 (86 %), parce que Vevidental
 *                 duplique le praticien seul en cabinet. C'est une DEDUCTION,
 *                 et elle peut se tromper.
 *
 * Melanger les trois sans le dire ferait passer une deduction pour une
 * relation etablie. L'ecran doit pouvoir le montrer, donc le serveur doit le
 * rendre.
 */
export type OriginePraticien = "travaux" | "fiche" | "homonyme";

/** Un dentiste propose au titre d'un cabinet, avec la raison de sa presence. */
export interface PraticienDuCabinet extends OptionPraticien {
  origine: OriginePraticien;
}

/**
 * Le corps de `POST /partage/lieux`.
 *
 * On désigne le praticien par son identifiant VEVIDENTAL, pas par son identité
 * de registre : au moment où l'utilisateur choisit dans la liste, cette
 * identité n'existe pas encore pour la plupart des dentistes. C'est le serveur
 * qui la crée au passage, à la demande — cohérent avec D23, où les accès
 * naissent au fil des besoins et jamais en campagne.
 */
/**
 * ⚠ UN LIEU A AU MOINS UN PROPRIÉTAIRE, PAS FORCÉMENT UN PRATICIEN — 20/08.
 *
 * `praticienIdVevidental` était obligatoire. Vevidental ne renseigne pourtant le
 * dentiste que pour 29 % des travaux : 551 cabinets actifs n'en ont AUCUN, et
 * leurs clients étaient donc inatteignables. C'est le mur sur lequel Dove a buté
 * en essayant la v1.71.0 avec « ABENSUR et Associé » — 646 travaux, 420
 * patients, et un formulaire qui n'affichait plus rien.
 *
 * `refine` plutôt que deux schémas : une seule règle, énoncée une fois. La base
 * porte la même (`ck_partage_lieu_a_un_proprietaire`, migration 0037) — deux
 * garde-fous, parce que l'API est atteignable sans passer par l'écran.
 */
export const CreerLieuSchema = z
  .object({
    /** Absent = lieu d'un CABINET dont aucun dentiste n'est connu. */
    praticienIdVevidental: IdVevidentalSchema.optional(),
    /** Absent = exercice privé du praticien, sans cabinet rattaché (D7). */
    cabinetIdVevidental: IdVevidentalSchema.optional(),
    libelle: z.string().trim().min(1).max(120),
  })
  .refine((d) => d.praticienIdVevidental !== undefined || d.cabinetIdVevidental !== undefined, {
    message: "un lieu appartient à un praticien, à un cabinet, ou aux deux — jamais à rien",
  });
